// =============================================================================
// Lumen Viewer — implant template library (placeholder / stub)
// =============================================================================
// This is a deliberately minimal library: generic SVG outlines for the
// most common ortho-preop scenarios, scaled by calibration (mm/px).
// Replace with a real vendor-licensed library (Stryker / Zimmer / DePuy /
// Smith & Nephew / etc.) before clinical go-live — each manufacturer
// supplies templates at specific sizes; licensing is legally mandatory.
//
// Each template is a function (mmPerPx, opts) -> {
//   svg: "<g>...</g>",            // placed inside the overlay canvas
//   width_mm, height_mm, label
// }

export const CATEGORIES = {
  HIP_CUP:      'Acetabular cup (THA)',
  HIP_STEM:     'Femoral stem (THA)',
  HIP_HEAD:     'Femoral head (THA)',
  KNEE_FEMORAL: 'Femoral component (TKA)',
  KNEE_TIBIAL:  'Tibial tray (TKA)',
  KNEE_PATELLA: 'Patellar button (TKA)',
  CANNULATED:   'Cannulated screw',
  DHS_PLATE:    'DHS side plate',
  IM_NAIL:      'Intramedullary nail',
};

// Generic acetabular cup — hemisphere outline at given diameter.
function hipCup(diameter_mm) {
  return (mmPerPx) => {
    const r = (diameter_mm / 2) / mmPerPx;
    const svg = `<g stroke="#2dd4a0" stroke-width="1.5" fill="rgba(45,212,160,0.08)">
      <path d="M ${-r} 0 A ${r} ${r} 0 0 1 ${r} 0 L ${r * 0.9} ${r * 0.1} L ${-r * 0.9} ${r * 0.1} Z" />
      <circle cx="0" cy="0" r="${r * 0.15}" fill="#2dd4a0" />
    </g>`;
    return { svg, width_mm: diameter_mm, height_mm: diameter_mm / 2, label: `Cup ${diameter_mm}mm` };
  };
}

// Generic femoral stem — tapered wedge with collar.
function hipStem(length_mm = 140, width_top = 14, width_bot = 8) {
  return (mmPerPx) => {
    const L = length_mm / mmPerPx;
    const wT = width_top / mmPerPx, wB = width_bot / mmPerPx;
    const svg = `<g stroke="#2dd4a0" stroke-width="1.5" fill="rgba(45,212,160,0.08)">
      <path d="M ${-wT / 2} 0 L ${wT / 2} 0 L ${wB / 2} ${L} L ${-wB / 2} ${L} Z" />
      <line x1="0" y1="0" x2="0" y2="${L}" stroke-dasharray="4 4" opacity="0.6" />
    </g>`;
    return { svg, width_mm: width_top, height_mm: length_mm, label: `Stem ${length_mm}×${width_top}mm` };
  };
}

// TKA femoral component — curved cap + pegs.
function kneeFemoral(width_mm = 65, height_mm = 55) {
  return (mmPerPx) => {
    const w = width_mm / mmPerPx, h = height_mm / mmPerPx;
    const svg = `<g stroke="#2dd4a0" stroke-width="1.5" fill="rgba(45,212,160,0.08)">
      <path d="M ${-w / 2} 0
               Q ${-w / 2} ${-h} 0 ${-h}
               Q ${w / 2} ${-h} ${w / 2} 0 Z" />
    </g>`;
    return { svg, width_mm, height_mm, label: `TKA femoral ${width_mm}mm` };
  };
}

// TKA tibial tray — flat platform with stem.
function kneeTibial(width_mm = 70, stem_len = 40) {
  return (mmPerPx) => {
    const w = width_mm / mmPerPx, s = stem_len / mmPerPx;
    const svg = `<g stroke="#2dd4a0" stroke-width="1.5" fill="rgba(45,212,160,0.08)">
      <rect x="${-w / 2}" y="0" width="${w}" height="${5 / mmPerPx}" />
      <rect x="${-w / 16}" y="${5 / mmPerPx}" width="${w / 8}" height="${s}" />
    </g>`;
    return { svg, width_mm, height_mm: stem_len + 5, label: `TKA tibial ${width_mm}mm` };
  };
}

// Cannulated screw
function cannulatedScrew(length_mm = 60, diameter_mm = 7) {
  return (mmPerPx) => {
    const L = length_mm / mmPerPx, d = diameter_mm / mmPerPx;
    const svg = `<g stroke="#2dd4a0" stroke-width="1" fill="rgba(45,212,160,0.1)">
      <rect x="${-d / 2}" y="0" width="${d}" height="${L}" />
    </g>`;
    return { svg, width_mm: diameter_mm, height_mm: length_mm, label: `Screw ${length_mm}×${diameter_mm}mm` };
  };
}

export const CATALOG = [
  { id: 'cup-50',       category: CATEGORIES.HIP_CUP,      build: hipCup(50)         },
  { id: 'cup-52',       category: CATEGORIES.HIP_CUP,      build: hipCup(52)         },
  { id: 'cup-54',       category: CATEGORIES.HIP_CUP,      build: hipCup(54)         },
  { id: 'cup-56',       category: CATEGORIES.HIP_CUP,      build: hipCup(56)         },
  { id: 'cup-58',       category: CATEGORIES.HIP_CUP,      build: hipCup(58)         },
  { id: 'cup-60',       category: CATEGORIES.HIP_CUP,      build: hipCup(60)         },
  { id: 'stem-s',       category: CATEGORIES.HIP_STEM,     build: hipStem(130, 12, 7)},
  { id: 'stem-m',       category: CATEGORIES.HIP_STEM,     build: hipStem(145, 14, 8)},
  { id: 'stem-l',       category: CATEGORIES.HIP_STEM,     build: hipStem(160, 16, 9)},
  { id: 'knee-fem-sm',  category: CATEGORIES.KNEE_FEMORAL, build: kneeFemoral(60, 50)},
  { id: 'knee-fem-md',  category: CATEGORIES.KNEE_FEMORAL, build: kneeFemoral(65, 55)},
  { id: 'knee-fem-lg',  category: CATEGORIES.KNEE_FEMORAL, build: kneeFemoral(70, 58)},
  { id: 'knee-tib-sm',  category: CATEGORIES.KNEE_TIBIAL,  build: kneeTibial(65, 35)},
  { id: 'knee-tib-md',  category: CATEGORIES.KNEE_TIBIAL,  build: kneeTibial(70, 40)},
  { id: 'knee-tib-lg',  category: CATEGORIES.KNEE_TIBIAL,  build: kneeTibial(75, 45)},
  { id: 'screw-30x4',   category: CATEGORIES.CANNULATED,   build: cannulatedScrew(30, 4)},
  { id: 'screw-45x5',   category: CATEGORIES.CANNULATED,   build: cannulatedScrew(45, 5)},
  { id: 'screw-60x7',   category: CATEGORIES.CANNULATED,   build: cannulatedScrew(60, 7)},
  { id: 'screw-80x7',   category: CATEGORIES.CANNULATED,   build: cannulatedScrew(80, 7)},
];
